import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Globe, Music, Calendar, Users, Trophy, Heart } from 'lucide-react';
import { highlights } from '../mock';

const iconMap = {
  Globe: Globe,
  Music: Music,
  Calendar: Calendar,
  Users: Users,
  Trophy: Trophy,
  Heart: Heart,
};

export const HighlightsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      ref={ref}
      className="relative py-24 lg:py-32 bg-black overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(139, 92, 246) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-7xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-purple-400 to-green-400 mb-6 uppercase">
            Our Journey
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-green-400 via-purple-500 to-green-400 mx-auto mb-6" />
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Milestones that define our passion and dedication to worship music
          </p>
        </motion.div>

        {/* Highlights Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {highlights.map((highlight) => {
            const IconComponent = iconMap[highlight.icon];
            return (
              <motion.div
                key={highlight.id}
                variants={cardVariants}
                className="group relative bg-gradient-to-br from-purple-900/30 to-gray-900/30 backdrop-blur-lg border border-purple-500/20 rounded-3xl p-8 hover:border-green-400/60 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/30 cursor-pointer overflow-hidden"
              >
                {/* Animated Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 via-purple-500/0 to-green-500/0 group-hover:from-green-500/10 group-hover:via-purple-500/10 group-hover:to-green-500/10 rounded-3xl transition-all duration-500" />
                
                {/* Glow Effect */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-green-500/20 rounded-full blur-3xl group-hover:bg-green-500/30 transition-all duration-500" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl group-hover:bg-purple-500/30 transition-all duration-500" />

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6 inline-flex p-4 bg-gradient-to-br from-purple-600/30 to-green-600/30 rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-10 h-10 text-green-400 group-hover:text-green-300 transition-colors duration-300" />
                  </div>

                  {/* Year Badge */}
                  <div className="inline-block mb-4 px-4 py-1 bg-purple-500/20 border border-purple-400/30 rounded-full">
                    <span className="text-sm font-mono text-purple-300 uppercase tracking-wider">
                      {highlight.year}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-green-400 transition-colors duration-300">
                    {highlight.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {highlight.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
